import { Component, OnInit, OnDestroy } from '@angular/core';
import { ColDef, GridApi, GridReadyEvent, GridOptions, ApplyColumnStateParams } from 'ag-grid-community';
import { AgGridOptionsBuilder, AgGridFilterParams, AgGridColumns } from '../../../Compliance/AgGrid';
import { DataHubLogListAgGridDataSource } from './agGridDataSource';
import { WeissmanTimestampMsecFormatPipe } from '../../../UI-Lib/Pipes/Date-Format/date-formatting.pipe';
import { Subscription } from 'rxjs';
import { DataHubLogService } from '../dataHubLog.service';
import { DataHubLogRepository } from '../dataHubLog.repository';
import { DataHubLogListGridActionCellRendererComponent, DataHubLogListGridActionCellRendererParams } from './agGridActionCellRenderer.component';
import { WeissmanModalService } from '../../../Compliance/WeissmanModalService';
import { DataHubLogDetailsModalComponent, DataHubLogDetailsParams } from '../Data-Hub-Log-Details/dataHubLogDetailsModal.component';
import { BusyIndicatorService } from '../../../Busy-Indicator';
import { RestrictService, Roles } from '../../../Common/Permissions/restrict.service';
import { UpgradeNavigationServiceHandler } from '../../../Common/Routing/upgrade-navigation-handler.service';

@Component({
    selector: 'data-hub-log-list',
    templateUrl: './dataHubLogList.component.html',
    styleUrls: ['./dataHubLogList.component.scss'],
    standalone: false
})
export class DataHubLogListComponent implements OnInit, OnDestroy {
    constructor(
        private readonly _timestampPipe: WeissmanTimestampMsecFormatPipe,
        private readonly _dataHubLogRepository: DataHubLogRepository,
        private readonly _dataHubLogService: DataHubLogService,
        private readonly _modalService: WeissmanModalService,
        private readonly _busyService: BusyIndicatorService,
        private readonly _restrictService: RestrictService,
        private readonly _routerService: UpgradeNavigationServiceHandler) {
    }

    gridId: System.Guid = '59F77F2D-C744-455F-ABEA-891464CA7E37';
    isInitialized: boolean = false;
    gridOptions: GridOptions = new AgGridOptionsBuilder()
        .withColumnPinning()
        .buildDefault(this);
    gridApi: GridApi;

    private _gridDataSource: DataHubLogListAgGridDataSource;
    private _refreshSub: Subscription;

    get refreshing(): boolean {
        return this._gridDataSource && this._gridDataSource.isRefreshing;
    }

    ngOnInit(): void {
        if (!(this._restrictService.isInRole(Roles.ADMINOPERATIONSVIEW) || this._restrictService.isInRole(Roles.ADMINOPERATIONSEDIT))) {
            this.navigateToUnauthorized();
            return;
        }

        this._refreshSub = this._dataHubLogService.refresh$.subscribe(() => {
            this._refreshDataSource();
        });

        this.isInitialized = true;
    }

    ngOnDestroy(): void {
        this._refreshSub && this._refreshSub.unsubscribe();
    }

    onAgGridReady(event: GridReadyEvent): void {
        this.gridApi = event.api;

        const columns: ColDef[] = [
            {
                headerName: 'Summary ID',
                field: 'dataHubSummaryLogId',
                width: AgGridColumns.textColumnSmallWidth,
                maxWidth: AgGridColumns.textColumnMedWidth
            },
            {
                headerName: 'Operaton Type',
                field: 'operationType',
                width: AgGridColumns.textColumnMedWidth,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams
            },
            {
                headerName: 'Start Time',
                field: 'startTime',
                width: AgGridColumns.textColumnMedWidth,
                filter: 'agDateColumnFilter',
                filterParams: AgGridFilterParams.dateFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.dateFloatingFilterParams,
                valueFormatter: (params) => {
                    const model = params.data as Core.DataHubSummaryLogModel;
                    if (!(model && model.startTime)) {
                        return '';
                    }

                    return this._timestampPipe.transform(model.startTime);
                }
            },
            {
                headerName: 'End Time',
                field: 'endTime',
                width: AgGridColumns.textColumnMedWidth,
                filter: 'agDateColumnFilter',
                filterParams: AgGridFilterParams.dateFilterParamsWithBlankOptionsParams,
                floatingFilterComponentParams: AgGridFilterParams.dateFloatingFilterParams,
                valueFormatter: (params) => {
                    const model = params.data as Core.DataHubSummaryLogModel;
                    if (!(model && model.endTime)) {
                        return '';
                    }

                    return this._timestampPipe.transform(model.endTime);
                }
            },
            {
                headerName: 'Payment Batch Id',
                field: 'paymentBatchId',
                //type: 'numericColumn',
                width: AgGridColumns.textColumnSmallWidth,
                filter: 'agNumberColumnFilter',
                filterParams: AgGridFilterParams.numberFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.numberFloatingFilterParams
            },
            {
                headerName: 'Package #',
                field: 'packageNumber',
                width: AgGridColumns.textColumnSmallWidth,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams
            },
            {
                headerName: 'Request Payload',
                field: 'requestPayloadJson',
                width: AgGridColumns.textColumnWidth,
                maxWidth: AgGridColumns.textColumnWidth,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterWithBlankOptionsParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                sortable: false
            },
            {
                headerName: 'Response Payload',
                field: 'responsePayloadJson',
                width: AgGridColumns.textColumnWidth,
                maxWidth: AgGridColumns.textColumnWidth,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterWithBlankOptionsParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                sortable: false
            },
            {
                headerName: '',
                field: 'actions',
                pinned: 'right',
                width: AgGridColumns.getActionColumnWidth(1),
                minWidth: AgGridColumns.getActionColumnWidth(1),
                maxWidth: AgGridColumns.getActionColumnWidth(1),
                suppressSizeToFit: true,
                suppressAutoSize: true,
                resizable: false,
                suppressColumnsToolPanel: true,
                lockPinned: true,
                sortable: false,
                cellRenderer: DataHubLogListGridActionCellRendererComponent,
                cellRendererParams: {
                    viewDetails: this._viewDetails.bind(this)
                } as DataHubLogListGridActionCellRendererParams
            }
        ];

        const defaultSortModel: ApplyColumnStateParams = {
            state: [{ colId: 'dataHubSummaryLogId', sort: 'desc' }],
            defaultState: { sort: null }
        };

        this.gridApi.setGridOption('paginationPageSize', 100);
        this.gridApi.setGridOption('columnDefs', columns);
        this.gridApi.applyColumnState(defaultSortModel);
        this._setDataSource();
    }

    async refresh(): Promise<void> {
        const busyRef = this._busyService.show({ message: 'Loading' });

        try {
            await this._dataHubLogService.refresh();
        } finally {
            busyRef.hide();
        }
    }

    navigateToUnauthorized(): void {
        this._routerService.go('unauthorizedAccess', {});
    }

    private async _viewDetails(params: DataHubLogListGridActionCellRendererParams): Promise<void> {
        const dataHubSummaryLog = params.data as Core.DataHubSummaryLogModel;
        if (!dataHubSummaryLog) {
            return;
        }

        const detailParams: DataHubLogDetailsParams = {
            summaryLogId: dataHubSummaryLog.dataHubSummaryLogId
        };

        await this._modalService.showAsync(DataHubLogDetailsModalComponent, detailParams, 'modal-lg');
    }

    private _refreshDataSource(): void {
        if (!this._gridDataSource) {
            const success = this._setDataSource();
            if (!success) {
                return;
            }
        }

        this._gridDataSource.refresh();
    }

    private _setDataSource(): boolean {
        if (!this.gridApi || this._gridDataSource) {
            return;
        }

        this._gridDataSource = new DataHubLogListAgGridDataSource(this.gridApi, this._dataHubLogRepository);
        this.gridApi.updateGridOptions({ datasource: this._gridDataSource });
        return true;
    }
}
